import { Button, Group, Modal, Stack, Text, ThemeIcon, List } from '@mantine/core';
import { IconCheck, IconPlus, IconLayoutDashboard, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { clientRoutes } from '@/ApiPaths';
import { useStore } from '@/store/Store';
import type { BatchCreateResult } from '@/hooks/useProductBatchCreate';

type ProductBatchSuccessModalProps = {
  result: BatchCreateResult | null;
  onDismiss: () => void;
  onCreateMore: () => void;
  /** Must release blob URLs / draft state before leaving the editor. */
  onGoToDashboard: () => void;
};

export function ProductBatchSuccessModal({
  result,
  onDismiss,
  onCreateMore,
  onGoToDashboard,
}: ProductBatchSuccessModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const color = useStore((state) => state.userProfile.qrCodeColor);

  const opened = Boolean(result);
  if (!result) return null;

  const createdCount = result.created.length;

  return (
    <Modal
      opened={opened}
      onClose={onDismiss}
      centered
      size="md"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      radius="lg"
    >
      <Stack align="center" gap="md" py="sm">
        <ThemeIcon
          size={56}
          radius="xl"
          color={createdCount > 0 ? color : 'red'}
          variant="light"
        >
          {createdCount > 0 ? <IconCheck size={28} /> : <IconAlertCircle size={28} />}
        </ThemeIcon>
        <Text fw={700} size="lg" ta="center">
          {createdCount > 0
            ? t('editProduct.batch.successTitle', { count: createdCount })
            : t('editProduct.batch.allFailedTitle')}
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          {createdCount > 0
            ? t('editProduct.batch.successMessage', { count: createdCount })
            : t('editProduct.batch.allFailedMessage')}
        </Text>

        {result.failed.length > 0 ? (
          <Stack gap={4} w="100%">
            <Text size="sm" fw={600} c="red">
              {t('editProduct.batch.failedTitle', { count: result.failed.length })}
            </Text>
            <List size="sm" spacing={4}>
              {result.failed.map((item) => (
                <List.Item key={item.productName}>
                  {item.productName}: {item.error}
                </List.Item>
              ))}
            </List>
          </Stack>
        ) : null}

        <Group justify="center" mt="sm">
          <Button
            variant="outline"
            color={color}
            leftSection={<IconLayoutDashboard size={16} />}
            onClick={() => {
              onGoToDashboard();
              navigate(clientRoutes.dashboard);
            }}
          >
            {t('editProduct.batch.goToDashboard')}
          </Button>
          <Button
            variant="subtle"
            color="gray"
            onClick={onDismiss}
          >
            {t('editProduct.batch.continueEditing')}
          </Button>
          <Button color={color} leftSection={<IconPlus size={16} />} onClick={onCreateMore}>
            {t('editProduct.batch.createMore')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
